import { createContext, useState, useEffect, useCallback, useRef } from "react";
import { useIdle } from "../hooks/useIdle";
import { CONFIG } from "../config/Config";
import { refreshAccessToken } from "../utils/token";
import { queryClient } from "../queryClient";

export const IdleContext = createContext(null);

const IDLE_TIME = 10 * 60 * 1000; // 10 minutes
const WARNING_COUNTDOWN_TIME = 5 * 60 * 1000; // 5 minutes

export const IdleProvider = ({ children }) => {
  const isIdle = useIdle(IDLE_TIME);
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(
    WARNING_COUNTDOWN_TIME / 1000
  );
  const [isRefreshingSession, setIsRefreshingSession] = useState(false);
  const [tabHiddenSince, setTabHiddenSince] = useState(null);
  const warningStartedAt = useRef(null);

  const resetIdleState = useCallback(() => {
    setShowIdleWarning(false);
    setRemainingTime(WARNING_COUNTDOWN_TIME / 1000);
    warningStartedAt.current = null;
  }, []);

  useEffect(() => {
    if (isIdle && !showIdleWarning) {
      setShowIdleWarning(true);
      warningStartedAt.current = Date.now();
    }
  }, [isIdle, showIdleWarning]);

  useEffect(() => {
    let countdownInterval;

    if (showIdleWarning && warningStartedAt.current) {
      const updateRemainingTime = () => {
        const elapsed = Date.now() - warningStartedAt.current;
        const remaining = Math.max(
          0,
          Math.ceil((WARNING_COUNTDOWN_TIME - elapsed) / 1000)
        );
        setRemainingTime(remaining);
        if (remaining <= 0) {
          clearInterval(countdownInterval);
        }
      };

      updateRemainingTime();
      countdownInterval = setInterval(updateRemainingTime, 1000);
    }

    return () => {
      if (countdownInterval) {
        clearInterval(countdownInterval);
      }
    };
  }, [showIdleWarning]);

  const logout = useCallback(async () => {
    try {
      const response = await fetch(`${CONFIG.API_BASE_PATH}/refresh`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        window.location.href = `${CONFIG.API_BASE_PATH}/identity-logout`;
      } else {
        queryClient.clear();
        window.location.href = CONFIG.SSO_REDIRECT_PATH;
      }
    } catch (error) {
      console.warn('Session check failed during logout, redirecting to SSO:', error);
      queryClient.clear();
      window.location.href = CONFIG.SSO_REDIRECT_PATH;
    }
  }, []);

  useEffect(() => {
    if (showIdleWarning && remainingTime === 0) {
      logout();
    }
  }, [showIdleWarning, remainingTime, logout]);

  // Track page visibility to detect when tab is hidden/shown
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is now hidden - record the timestamp
        setTabHiddenSince(Date.now());
      } else {
        if (tabHiddenSince) {
          const hiddenDuration = Date.now() - tabHiddenSince;
          setTabHiddenSince(null);

          if (hiddenDuration > IDLE_TIME && !showIdleWarning) {
            console.debug(`Tab was hidden for ${Math.round(hiddenDuration / 1000)}s, checking session validity`);

            refreshAccessToken()
              .then(() => {
                warningStartedAt.current = Date.now();
                setShowIdleWarning(true);
              })
              .catch(() => {
                console.debug('Session expired during tab-hidden period, redirecting via session expiration handler');
              });
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [tabHiddenSince, showIdleWarning]);

  useEffect(() => {
    const handleUserActivity = (event) => {
      // Don't reset idle state if we're in the middle of refreshing the session
      if (showIdleWarning && !isRefreshingSession && !isEventOnIdleDialog(event)) {
        resetIdleState();
      }
    };

    const isEventOnIdleDialog = (event) => {
      const idleDialogElement = document.querySelector(".idle-dialog");
      if (!idleDialogElement) {
        return false;
      }

      if (event.target && idleDialogElement.contains(event.target)) {
        return true;
      }

      if (event.clientX && event.clientY) {
        const rect = idleDialogElement.getBoundingClientRect();
        if (
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom
        ) {
          return true;
        }
      }

      return false;
    };

    const activityEvents = [
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
    ];

    activityEvents.forEach((eventType) => {
      window.addEventListener(eventType, handleUserActivity, { passive: true });
    });

    return () => {
      activityEvents.forEach((eventType) => {
        window.removeEventListener(eventType, handleUserActivity);
      });
    };
  }, [showIdleWarning, isRefreshingSession, resetIdleState]);

  const idleContext = {
    isIdle,
    showIdleWarning,
    remainingTime,
    resetIdleState,
    logout,
    isRefreshingSession,
    setIsRefreshingSession,
  };

  return (
    <IdleContext.Provider value={idleContext}>{children}</IdleContext.Provider>
  );
};